import React, { useState, useEffect, useRef } from "react";
import ModalNew from "../popup/Modal";

const AccountSectionEditInPlaceWrapper = ({ 
  title, 
  isEditMode, 
  onToggleEdit,
  onApplyChanges,
  onDiscardChanges,
  hasChanges = false,
  infoTooltip = null, // New prop for tooltip content
  section = null, // New prop for API section name (e.g., 'skills_expertise', 'availability')
  preferencesData = null, // New prop for current preferences data
  children 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const tooltipRef = useRef(null);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  const handleSwitchToggle = () => {
    if (isEditMode && hasChanges) {
      // Show confirmation modal if there are unsaved changes
      setShowConfirmModal(true);
    } else {
      // No changes, safe to toggle
      onToggleEdit();
    }
  };

  const handleConfirmExit = () => {
    setShowConfirmModal(false);
    onDiscardChanges(); // Reset to initial state
    onToggleEdit(); // Turn off edit mode
  };

  const handleGoBack = () => {
    setShowConfirmModal(false);
    // Stay in edit mode
  };

  const handleSavePreferences = () => {
    // Just call the parent's onApplyChanges - the parent will handle the API call and modal
    if (onApplyChanges) {
      onApplyChanges();
    }
  };

  return (
    <div className={`account-section-wrapper ${isEditMode ? 'edit-mode' : ''}`}>
      <div className="section-header">
        <div className="section-title-container">
          <h3 className="section-title">{title}</h3>
          {infoTooltip && (
            <div className="info-icon-container" ref={tooltipRef}>
              <img 
                src={`${process.env.PUBLIC_URL}/images/icons/info_blue.svg`}
                alt="Info"
                className="info-icon"
                onClick={() => setShowTooltip(!showTooltip)}
              />
              {showTooltip && (
                <div className="tooltip">
                  <p>{infoTooltip}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="section-actions">
          <div className="switch-container">
            <input
              type="checkbox"
              id={`switch-${title.toLowerCase().replace(/\s+/g, '-')}`}
              className="edit-switch"
              checked={isEditMode}
              onChange={handleSwitchToggle}
            />
            <label 
              htmlFor={`switch-${title.toLowerCase().replace(/\s+/g, '-')}`}
              className="switch-label"
            >
              <span className="switch-slider"></span>
            </label>
          </div>
        </div>
      </div>
      <div className="section-content">
        {children}
      </div>
      {isEditMode && (
        hasChanges ? (
            <div className="button-group">
              <button
                className="cta-button secondary short text-center"
                onClick={onDiscardChanges}
              >
                Discard
              </button>
              <button
                className="cta-button primary short text-center"
                onClick={handleSavePreferences}
              >
                Apply Changes
              </button>
            </div>
        ) : (
          <div className="no-changes-message">
            No changes to apply
          </div>
        )
      )}

      {/* Confirmation Modal for Unsaved Changes */}
      {showConfirmModal && (
        <ModalNew className="confirmation-modal">
          <div className="modal-form">
            <p style={{ textAlign: 'center' }}>You have unsaved changes. Are you sure you want to exit edit mode?</p>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button 
                className="cta-button secondary short text-center"
                onClick={handleGoBack}
              >
                Go Back
              </button>
              <button 
                className="cta-button primary short text-center"
                onClick={handleConfirmExit}
              >
                Confirm
              </button>
            </div>
          </div>
        </ModalNew>
      )}
    </div>
  );
};

export default AccountSectionEditInPlaceWrapper;
